import { useNotification } from "@expert/common-ui";
import {
    addChatSmartOfferGenericMessage,
    addChatSmartOfferResponseMessage,
} from "@soluto-private/expert-workspace-timeline";
import { HTTPError } from "ky";
import { useCallback, useMemo, useState } from "react";

import { getRootDispatcher } from "@/analytics";
import { useVerifiedCustomerStore } from "@/customer-info/store";
import { postDeclination } from "@/sales-tools/home-product/api";
import { useHandleSendLink } from "@/sales-tools/home-product/components/SendLink/HandleSendLink";
import { type EligibleProduct, useProductOfferStore } from "@/sales-tools/home-product/stores";
import { SaleProgress } from "@/sales-tools/home-product/types";
import { type ChatTask, usePartner, useSendChatMessage, useSessionId } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";

import { disclosureModalRef } from "./DisclosureModalController";
import { useSmartOfferFlowStore } from "./useSmartOfferFlowStore";

// Helper function to format and validate customer contact
function formatAndValidateContact(mdn: string | undefined) {
    const sanitizedMdn = mdn?.replace(/\D/g, "");
    return sanitizedMdn ? `+1${sanitizedMdn}` : undefined;
}

export function useSmartOfferActions(selectedProduct: EligibleProduct | undefined, chatTask: ChatTask | undefined) {
    const logger = useLogger({
        module: "useSmartOfferActions",
        supportTeam: "messaging",
    });
    const sessionId = useSessionId();
    const partner = usePartner();
    const notification = useNotification();
    const sendLink = useHandleSendLink();
    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const [isResendLinkLoading, setIsResendLinkLoading] = useState(false);
    const { sendChatMessage } = useSendChatMessage({
        chatTask: chatTask,
        chatId: chatTask?.chatId,
        sourceComponent: "assist-timeline-smart-offer",
    });
    const currentStep = useSmartOfferFlowStore((state) => state.getCurrentStep(sessionId));
    const updateProduct = useProductOfferStore((state) => state.updateProduct);
    const updateStep = useSmartOfferFlowStore((state) => state.updateStep);
    const markActionAsUsed = useSmartOfferFlowStore((state) => state.markActionAsUsed);
    const isSmartOfferActionUsed = useSmartOfferFlowStore((state) => state.isSmartOfferActionUsed);

    // Check if maximum send link attempts reached
    const isMaxAttemptsReached = useProductOfferStore((state) =>
        selectedProduct
            ? state.getSmartLinkMetaBySessionId(sessionId, selectedProduct.id).maximumSmartLinksGenerated
            : false,
    );

    // Get product info which contains the messages
    const productInfo = selectedProduct
        ? useProductOfferStore.getState().getProductInfo(sessionId, selectedProduct.productSku)
        : undefined;

    const handleChatSendToCustomer = useCallback(async () => {
        if (!selectedProduct || !chatTask || !productInfo) {
            logger.warn({ sessionId }, "Cannot send offer: no product, chat task, or product info");
            return;
        }

        try {
            logger.info({ sessionId, productSku: selectedProduct.productSku }, "Sending offer to customer");

            // Check if current step is celebratory
            if (currentStep === "CelebratoryMessageStep") {
                // Send congratulatory message
                await sendChatMessage(
                    "Congratulations on enrolling! Be on the lookout for a welcome email within the next few days with complete details about all the amazing benefits!",
                );
                addChatSmartOfferResponseMessage(sessionId, "Congratulatory message sent");
                void getRootDispatcher().dispatchBusinessEvent("SalesSmartOfferAction", {
                    stepName: currentStep,
                    buttonAction: "sendCongratulations",
                    productSku: selectedProduct.productSku,
                    sessionId,
                });
                logger.info({ sessionId }, "Congratulatory message sent to customer successfully");
                markActionAsUsed(sessionId, "sendCongratulations");
            } else {
                // Send offer message
                await sendChatMessage(productInfo.chatSmartOfferMessage || "");
                addChatSmartOfferResponseMessage(sessionId, "Offer sent");
                updateStep(sessionId, selectedProduct.productSku, "SuggestedEnrollmentStep");
                void getRootDispatcher().dispatchBusinessEvent("SalesSmartOfferAction", {
                    stepName: currentStep,
                    buttonAction: "sendOffer",
                    productSku: selectedProduct.productSku,
                    sessionId,
                });
                logger.info({ sessionId }, "Offer sent to customer successfully");
                markActionAsUsed(sessionId, "sendOffer");
            }
        } catch (error) {
            logger.error({ error, sessionId }, "Failed to send offer to customer");
        }
    }, [
        logger,
        sessionId,
        selectedProduct,
        chatTask,
        productInfo,
        updateStep,
        sendChatMessage,
        currentStep,
        markActionAsUsed,
    ]);

    const handleChatSendEnrollment = useCallback(async () => {
        if (!selectedProduct || !chatTask || !productInfo) {
            logger.warn({ sessionId }, "Cannot send enrollment: no product, chat task, or product info");
            return;
        }

        try {
            logger.info({ sessionId, productSku: selectedProduct.productSku }, "Sending enrollment to customer");

            // 1. Send enrollment message to customer
            await sendChatMessage(productInfo.chatEnrollmentMessage || "");
            logger.info({ sessionId }, "Enrollment message sent to customer");
            void getRootDispatcher().dispatchBusinessEvent("SalesSmartOfferAction", {
                stepName: currentStep,
                buttonAction: "sendEnrollment",
                productSku: selectedProduct.productSku,
                sessionId,
            });
            addChatSmartOfferResponseMessage(sessionId, "Enrollment sent");
            markActionAsUsed(sessionId, "sendEnrollment");

            // 2. Format MDN properly before sending
            const contactToSend = formatAndValidateContact(customer?.mdn);

            if (!contactToSend) {
                logger.warn({ sessionId }, "No customer MDN available to send disclosure link");
                addChatSmartOfferGenericMessage(sessionId, "Failed to send: No customer contact");
                return;
            }

            // 3. Send the disclosure link - wrap in try-catch to handle specific sendLink errors
            try {
                logger.info({ sessionId }, "Attempting to send disclosure link");
                const response = await sendLink("SMS", contactToSend);
                logger.info({ sessionId, response }, "Disclosure link API response received");

                // 4. Update store with max attempts info
                useProductOfferStore.getState().updateSmartLinkMetaBySessionId(sessionId, selectedProduct.id, {
                    maximumSmartLinksGenerated: response.maxAttemptsReached,
                });

                logger.info({ sessionId }, "Store updated with smart link metadata");
            } catch (linkError) {
                logger.warn(
                    { error: linkError, sessionId },
                    "Error sending disclosure link, but continuing to next step",
                );

                if (linkError instanceof HTTPError && linkError.response.status === 403) {
                    notification.error("You have reached the maximum number of attempts to send a link.");
                } else {
                    notification.error("Failed to send link. Please try again.");
                }
            }

            // 5. Move to waiting step AFTER link attempt
            updateStep(sessionId, selectedProduct.productSku, "WaitingOnEnrollmentStep");

            logger.info({ sessionId }, "Enrollment sent to customer successfully");
        } catch (error) {
            logger.error({ error, sessionId }, "Error in handleChatSendEnrollment");
        }
    }, [
        logger,
        sessionId,
        selectedProduct,
        chatTask,
        productInfo,
        updateStep,
        sendChatMessage,
        sendLink,
        customer,
        notification,
        currentStep,
        markActionAsUsed,
    ]);

    const handleChatDeclineOffer = useCallback(async () => {
        if (!selectedProduct) {
            logger.warn({ sessionId }, "Cannot decline: no product selected");
            return;
        }

        // Don't allow declining if already successful
        if (selectedProduct.saleProgress === SaleProgress.Success) {
            logger.warn({ sessionId }, "Cannot decline: enrollment already successful");
            addChatSmartOfferGenericMessage(sessionId, "Cannot decline: customer already enrolled");
            return;
        }

        try {
            logger.info({ sessionId, productSku: selectedProduct.productSku }, "Declining offer");

            await postDeclination(logger, {
                sessionId,
                partner,
                productOfferId: selectedProduct.id,
                productSku: selectedProduct.productSku,
            });

            updateProduct(sessionId, selectedProduct.id, {
                saleProgress: SaleProgress.Declined,
            });

            void getRootDispatcher().dispatchBusinessEvent("SalesSmartOfferAction", {
                stepName: currentStep,
                buttonAction: "decline",
                productSku: selectedProduct.productSku,
                sessionId,
            });
            markActionAsUsed(sessionId, "decline");

            logger.info({ sessionId }, "Offer declined successfully by expert");
        } catch (error) {
            logger.warn({ error }, "Error declining enrollment");
        }
    }, [logger, sessionId, partner, selectedProduct, updateProduct, currentStep, markActionAsUsed]);

    const handleResendDisclosureLink = useCallback(async () => {
        if (!selectedProduct || !chatTask || !productInfo) {
            logger.warn({ sessionId }, "Cannot resend disclosure link: no product, chat task, or product info");
            return;
        }

        if (selectedProduct.saleProgress === SaleProgress.Success) {
            logger.warn({ sessionId }, "Cannot resend link: enrollment already successful");
            addChatSmartOfferGenericMessage(sessionId, "Cannot resend: customer already enrolled");
            return;
        }

        setIsResendLinkLoading(true);
        try {
            logger.info({ sessionId, productSku: selectedProduct.productSku }, "Resending enrollment link to customer");

            const contactToSend = formatAndValidateContact(customer?.mdn);

            if (!contactToSend) {
                logger.warn({ sessionId }, "No customer MDN available to resend enrollment link");
                addChatSmartOfferGenericMessage(sessionId, "Failed to resend: No customer contact info");
                return;
            }

            const response = await sendLink("SMS", contactToSend);

            useProductOfferStore.getState().updateSmartLinkMetaBySessionId(sessionId, selectedProduct.id, {
                maximumSmartLinksGenerated: response.maxAttemptsReached,
            });

            addChatSmartOfferGenericMessage(sessionId, "Resent enrollment link");
            void getRootDispatcher().dispatchBusinessEvent("SalesSmartOfferAction", {
                stepName: currentStep,
                buttonAction: "resendLink",
                productSku: selectedProduct.productSku,
                sessionId,
            });

            logger.info({ sessionId }, "Resent enrollment link successfully");
        } catch (error) {
            if (error instanceof HTTPError && error.response.status === 403) {
                notification.error("You have reached the maximum number of attempts to send a link.");
            } else {
                notification.error("Failed to send link. Please try again.");
            }
            logger.warn({ error, sessionId }, "Failed to resend disclosure link");
        } finally {
            setIsResendLinkLoading(false);
        }
    }, [logger, sessionId, selectedProduct, chatTask, productInfo, sendLink, customer, notification, currentStep]);

    const handlePreviewDisclosures = useCallback(() => {
        if (!selectedProduct) {
            logger.warn({ sessionId }, "Cannot preview disclosures: no product selected");
            return;
        }

        logger.info({ sessionId, productSku: selectedProduct.productSku }, "Opening preview disclosures modal");

        disclosureModalRef.current?.(selectedProduct.productSku, selectedProduct.requiresNyDisclosures ?? false);

        void getRootDispatcher().dispatchBusinessEvent("SalesSmartOfferAction", {
            stepName: currentStep,
            buttonAction: "previewDisclosures",
            productSku: selectedProduct.productSku,
            sessionId,
        });
    }, [logger, sessionId, selectedProduct, currentStep]);

    return useMemo(
        () => ({
            handleChatSendToCustomer,
            handleChatSendEnrollment,
            handleChatDeclineOffer,
            handleResendDisclosureLink,
            handlePreviewDisclosures,
            isResendLinkLoading,
            isMaxAttemptsReached,
            isSmartOfferActionUsed: (actionId: string) => isSmartOfferActionUsed(sessionId, actionId),
        }),
        [
            handleChatSendToCustomer,
            handleChatSendEnrollment,
            handleChatDeclineOffer,
            handleResendDisclosureLink,
            handlePreviewDisclosures,
            isResendLinkLoading,
            isMaxAttemptsReached,
            isSmartOfferActionUsed,
            sessionId,
        ],
    );
}
